import React, { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { useIntl } from 'react-intl'
import { Fieldset, Group, Paper, SimpleGrid, Stack } from '@mantine/core'
import { Divider } from 'primereact/divider'
import { useParams } from 'react-router-dom'

import CompaniesService from '../../../../services/companies/companies.service'
import UsersService from '../../../../services/users/users.service'
import { ACTIVITY_TYPES } from '../../../../utils/constants/activityTypes'
import { ZONES } from '../../../../utils/constants/zones'
import LabeledDropdown from '../../../../components/common/form-components/labeled-dropdown/LabeledDropdown'
import LabeledCheckbox from '../../../../components/common/form-components/labeled-checkbox/LabeledCheckbox'
import LabeledText from '../../../../components/common/form-components/labeled-inputs/LabeledText'
import useInputs from '../../../../utils/hooks/useInputs'
import CenterService from '../../../../services/centers/center.service'

import './centerForm.scss'
import { CENTER_TYPES } from '../../../../utils/constants/centerTypes'
import swalAlert from '../../../../utils/helpers/swalAlert'
import AppContainer from '../../../../component/layout/AppContainer.tsx'
import AppInput from '../../../../component/form/AppInput.tsx'
import EntityDropdown from '../../../../component/form/EntityDropdown.tsx'
import AppCheckbox from '../../../../component/form/AppCheckbox.tsx'
import AppSimpleGrid from '../../../../component/layout/AppSimpleGrid.tsx'

const CenterDataForm = ({ centerId, inputs, onChangeInputs, updateInputs }) => {
  const { formatMessage: msg } = useIntl()

  const urlParams = new URLSearchParams(window.location.search)
  const urlCenterType = urlParams.get('centerType')

  const [companies, setCompanies] = useState([])
  const [mutual, setMutual] = useState({})
  const [users, setUsers] = useState([])

  const [originalCenterType, setOriginalCenterType] = useState(urlCenterType)

  useEffect(() => {
    getUsers()
    getCompanies()
    getCenterData()
  }, [])

  const confirmCenterTypeOnChange = async (e) => {
    const confirm = await swalAlert.warning(
      msg({ id: 'centerType.change.confirm.msg' }),
      msg({ id: 'button.cancel' }),
      msg({ id: 'button.confirm' }),
    )
    if (confirm?.isConfirmed) {
      onChangeInputs(e)
    }
  }

  const getCenterData = async () => {
    if (centerId && centerId !== 'undefined') {
      const response = await CenterService.getById(centerId)
      if (response) {
        updateInputs(response.resp)
        if (!originalCenterType)
          setOriginalCenterType(response.resp?.centerType)
      }
    } else {
      updateInputs({ centerType: urlCenterType })
    }
  }

  const getCompanies = async () => {
    const response = await CompaniesService.getAll({})
    if (response) {
      setCompanies(response.resp)
    }
  }

  const getUsers = async () => {
    const resp = await UsersService.getAll({})
    if (resp) {
      setUsers(resp?.resp)
    }
  }

  // const onChangeCompany = (e) => {
  //     // onHandleChange(e)
  //     setMutual(e.target.value?.mutual)
  // }

  return (
    <AppSimpleGrid cols={1}>
      <AppContainer>
        <AppSimpleGrid cols={4}>
          <AppInput
            withAsterisk
            label="name"
            name="basicData.name"
            value={inputs?.basicData?.name}
            onChange={onChangeInputs}
          />
          <EntityDropdown
            withAsterisk
            entity="centerType"
            label="centerType"
            name="centerType"
            local={false}
            value={inputs?.centerType}
            onChange={inputs?._id && confirmCenterTypeOnChange}
          />
          <EntityDropdown
            entity="activityType"
            label="activityType"
            name="basicData.activityType"
            value={inputs?.basicData?.activityType}
            onChange={onChangeInputs}
          />
          <EntityDropdown
            entity="supervisor"
            label="Supervisor"
            name="basicData.zoneLeader"
            value={inputs?.basicData?.zoneLeader?._id}
            onChange={onChangeInputs}
          />
        </AppSimpleGrid>
        <AppSimpleGrid cols={4}>
          <EntityDropdown
            withAsterisk
            entity="zone"
            label="zone"
            name="basicData.zone"
            value={inputs?.basicData?.zone}
            onChange={onChangeInputs}
          />
          <AppInput
            entity="zone"
            label="province"
            name="basicData.province"
            value={inputs?.basicData?.province}
            onChange={onChangeInputs}
          />
          <AppInput
            entity="zone"
            label="locality"
            name="basicData.locality"
            value={inputs?.basicData?.locality}
            onChange={onChangeInputs}
          />
          <AppInput
            entity="zone"
            label="postalCode"
            name="basicData.cp"
            value={inputs?.basicData?.cp}
            onChange={onChangeInputs}
          />
        </AppSimpleGrid>
        <AppSimpleGrid cols={4}>
          <AppInput
            entity="zone"
            label="address"
            name="basicData.address"
            value={inputs?.basicData?.address}
            onChange={onChangeInputs}
          />
          <AppInput
            entity="phone"
            label="phone"
            name="basicData.phone"
            value={inputs?.basicData?.phone}
            onChange={onChangeInputs}
          />
          <AppInput
            entity="mobile"
            label="mobilePhone"
            name="basicData.mobilPhone"
            value={inputs?.basicData?.mobilPhone}
            onChange={onChangeInputs}
          />
          <AppInput
            entity="email"
            label="email"
            name="basicData.email"
            value={inputs?.basicData?.email}
            onChange={onChangeInputs}
          />
        </AppSimpleGrid>

        {inputs?.centerType === 'OWNER' && (
          <AppSimpleGrid cols={2}>
            <EntityDropdown
              entity="company"
              label="company"
              name="basicData.company"
              value={inputs?.basicData?.company?._id}
              onChange={onChangeInputs}
            />
            <AppCheckbox
              size="lg"
              label="office"
              name="basicData.office"
              checked={inputs?.basicData?.office}
              onChange={onChangeInputs}
            />
          </AppSimpleGrid>
        )}

        {inputs?.centerType === 'FRANCHISE' && (
          <Stack mt="xl">
            <Fieldset legend={msg({ id: 'franchised' })}>
              <AppSimpleGrid cols={4}>
                <AppInput
                  label="email"
                  name="franchiseCompany.name"
                  value={inputs?.franchiseCompany?.name}
                  onChange={onChangeInputs}
                />
                <AppInput
                  label="phone"
                  name="franchiseCompany.phone"
                  value={inputs?.franchiseCompany?.phone}
                  onChange={onChangeInputs}
                />
                <AppInput
                  label="email"
                  name="franchiseCompany.email"
                  value={inputs?.franchiseCompany?.email}
                  onChange={onChangeInputs}
                />
                <AppInput
                  label="nif"
                  name="franchiseCompany.nif"
                  value={inputs?.franchiseCompany?.nif}
                  onChange={onChangeInputs}
                />
              </AppSimpleGrid>
              <AppSimpleGrid cols={4}>
                <AppInput
                  entity="zone"
                  label="address"
                  name="franchiseCompany.address"
                  value={inputs?.franchiseCompany?.address}
                  onChange={onChangeInputs}
                />
                <AppInput
                  entity="zone"
                  label="province"
                  name="franchiseCompany.province"
                  value={inputs?.franchiseCompany?.province}
                  onChange={onChangeInputs}
                />
                <AppInput
                  entity="zone"
                  label="locality"
                  name="franchiseCompany.locality"
                  value={inputs?.franchiseCompany?.locality}
                  onChange={onChangeInputs}
                />
                <AppInput
                  entity="zone"
                  label="postalCode"
                  name="franchiseCompany.cp"
                  value={inputs?.franchiseCompany?.cp}
                  onChange={onChangeInputs}
                />
              </AppSimpleGrid>
              <AppSimpleGrid cols={1}>
                <AppInput
                  entity="supervisor"
                  label="legal_representative"
                  name="franchiseCompany.legalRepresentative"
                  value={inputs?.franchiseCompany?.legalRepresentative}
                  onChange={onChangeInputs}
                />
              </AppSimpleGrid>
            </Fieldset>

            <Fieldset legend={msg({ id: 'contactPerson' })}>
              <AppSimpleGrid cols={3}>
                <AppInput
                  label="name"
                  name="franchiseContact.name"
                  value={inputs?.franchiseContact?.name}
                  onChange={onChangeInputs}
                />
                <AppInput
                  entity="phone"
                  label="phone"
                  name="franchiseContact.phone"
                  value={inputs?.franchiseContact?.phone}
                  onChange={onChangeInputs}
                />
                <AppInput
                  entity="email"
                  label="email"
                  name="franchiseContact.email"
                  value={inputs?.franchiseContact?.email}
                  onChange={onChangeInputs}
                />
              </AppSimpleGrid>
            </Fieldset>
          </Stack>
        )}
      </AppContainer>
    </AppSimpleGrid>
  )
}

export default CenterDataForm
